// src/components/auth/auth-provider.tsx
"use client"
import type { User, AppSettings, SystemNotification } from '@/types';
import React, { createContext, useState, ReactNode, useEffect, useRef } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { auth, db, getFirestore } from '@/lib/firebase';
import { onAuthStateChanged, signInWithEmailAndPassword, signOut, signInAnonymously, type User as FirebaseUser } from 'firebase/auth';
import { doc, getDoc, collection, getDocs, query, orderBy, limit, Timestamp, enableNetwork, onSnapshot } from 'firebase/firestore';
import { appRoutes } from '@/lib/app-routes';
import Image from 'next/image';
import { safeISOString } from '@/lib/date-utils';
import { useToast } from '@/hooks/use-toast';

interface AuthContextType extends AppSettings {
  user: User | null;
  users: User[];
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [settings, setSettings] = useState<AppSettings>({});
  const [loading, setLoading] = useState(true);
  const router = useRouter();
  const pathname = usePathname();
  const { toast } = useToast();
  const isSigningIn = useRef(false);
  
  // Armazena o momento em que esta instância do app foi carregada/iniciada
  const [sessionStartTime] = useState(Date.now());
  
  useEffect(() => {
    const handleOnline = () => {
      const firestore = getFirestore();
      enableNetwork(firestore);
    };
    window.addEventListener('online', handleOnline);
    if (navigator.onLine) handleOnline();
    return () => window.removeEventListener('online', handleOnline);
  }, []);

  // Monitoramento de "Kill Switch" para motoristas
  useEffect(() => {
    if (!user || user.role !== 'driver') return;

    const authSettingsRef = doc(db, "settings", "auth");
    const unsubscribe = onSnapshot(authSettingsRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        const forceLogoutAt = data.forceLogoutDriversAt as Timestamp;
        
        if (forceLogoutAt) {
          const resetTime = forceLogoutAt.toMillis();
          // Se o comando de reset foi disparado APÓS o início desta sessão, desloga
          if (resetTime > sessionStartTime) {
            console.log("[Auth] Comando de desconexão global recebido. Encerrando sessão...");
            toast({
              title: "Sessão Encerrada",
              description: "Sua sessão foi finalizada por um administrador. Por favor, faça login novamente.",
              variant: "destructive"
            });
            logout();
          }
        }
      }
    });

    return () => unsubscribe();
  }, [user, sessionStartTime, toast]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser: FirebaseUser | null) => {
      // Se estamos em uma rota de compartilhamento e não há usuário, tenta login anônimo
      if (!firebaseUser && pathname?.startsWith('/shared/') && !isSigningIn.current) {
        isSigningIn.current = true;
        try {
          await signInAnonymously(auth);
        } catch (error) {
          console.error("Erro no login anônimo:", error);
        } finally {
          isSigningIn.current = false;
        }
        return;
      }

      if (firebaseUser) {
        // Se for usuário anônimo, define um perfil limitado
        if (firebaseUser.isAnonymous) {
          setUser({
            id: firebaseUser.uid,
            name: "Visitante Externo",
            role: "assistant", // Perfil de visualização
            allowedRoutes: [],
            avatar: "",
          } as any);
          setLoading(false);
          return;
        }

        try {
          const userDocRef = doc(db, "users", firebaseUser.uid);
          const permissionsDocRef = doc(db, "permissions", firebaseUser.uid);
          const settingsDocRef = doc(db, "settings", "app");
          const allUsersQuery = query(collection(db, "users"));
          const notificationsQuery = query(
              collection(db, "users", firebaseUser.uid, "notifications"),
              orderBy("createdAt", "desc"),
              limit(20)
          );

          const [userDoc, permissionsDoc, settingsDoc, allUsersSnapshot, notificationsSnapshot] = await Promise.all([
            getDoc(userDocRef),
            getDoc(permissionsDocRef),
            getDoc(settingsDocRef),
            getDocs(allUsersQuery),
            getDocs(notificationsQuery),
          ]);
          
          if (settingsDoc.exists()) setSettings(settingsDoc.data() as AppSettings);
          
          const allUsersData = allUsersSnapshot.docs.map(d => ({id: d.id, ...d.data()}) as User);
          setAllUsers(allUsersData);

          if (userDoc.exists()) {
            const userData = userDoc.data();
            let allowedRoutes: string[] = [];

            if (permissionsDoc.exists() && permissionsDoc.data().allowedRoutes?.length > 0) {
              allowedRoutes = permissionsDoc.data().allowedRoutes;
            } else if (userData.role !== 'admin') {
              if (userData.role === 'driver') {
                  allowedRoutes = appRoutes.filter(r => r.category === 'Motorista' || r.path === '/dashboard/profile').map(r => r.path);
              } else {
                  allowedRoutes = appRoutes.filter(r => r.category === 'Gestão' || r.path === '/dashboard/profile').map(r => r.path);
              }
            } else {
                allowedRoutes = appRoutes.map(r => r.path);
            }
            
            const notifications = notificationsSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                createdAt: safeISOString(doc.data().createdAt)!,
            } as SystemNotification));

            setUser({ 
              id: userDoc.id,
              name: userData.name || "Usuário",
              email: userData.email || firebaseUser.email || "",
              role: userData.role || "driver",
              avatar: userData.avatar || '',
              department: userData.department,
              allowedRoutes,
              notifications,
              cnhNumber: userData.cnhNumber,
              cnhDueDate: safeISOString(userData.cnhDueDate),
              toxicologicoDueDate: safeISOString(userData.toxicologicoDueDate),
            } as User);
          }
        } catch (error) {
          console.error("Erro ao carregar dados do usuário:", error);
        } finally {
          setLoading(false);
        }
      } else {
        setUser(null);
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [pathname]);

  // Lógica de Redirecionamento
  useEffect(() => {
    if (loading) return;
    const isAuthRoute = pathname === '/login';
    const isSharedRoute = pathname?.startsWith('/shared/');
    
    if (!user && !isAuthRoute && !isSharedRoute) {
      router.push('/login');
    } else if (user && isAuthRoute && !user.name.includes("Visitante")) {
      router.push('/dashboard');
    }
  }, [user, loading, pathname, router]);

  const login = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
  };

  const logout = async () => {
    await signOut(auth);
    setUser(null);
    router.push('/login');
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-background text-foreground">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        <p className="mt-4 text-muted-foreground">Iniciando...</p>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, users: allUsers, login, logout, loading, ...settings }}>
      {children}
    </AuthContext.Provider>
  );
}
